import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  FacebookIcon,
  TwitterIcon,
  InstagramIcon,
  LinkedinIcon,
  MailIcon,
  PhoneIcon,
  MapPinIcon,
  SunIcon,
  MoonIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface SiteFooterProps {
  isMobile: boolean;
  onThemeToggle: () => void;
}

export default function SiteFooter({ isMobile, onThemeToggle }: SiteFooterProps) {
  return (
    <footer className="bg-card text-card-foreground border-t border-border">
      <div className="container py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Company Info */}
          <div>
            <div className="mb-4">
              <h3 className="text-lg font-bold">
                <span className="text-black dark:text-white">Machine</span>
                <span className="text-blue-600">Search</span>
                <span className="text-black dark:text-white">.in</span>
              </h3>
            </div>
            <p className="text-muted-foreground mb-6">
              India's marketplace for industrial machinery, connecting
              buyers and sellers across the country.
            </p>
            <div className={cn("flex space-x-4", isMobile ? 'flex-wrap gap-2' : '')}>
              <Button variant="ghost" size="icon" aria-label="Facebook">
                <FacebookIcon className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="icon" aria-label="Twitter">
                <TwitterIcon className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="icon" aria-label="Instagram">
                <InstagramIcon className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="icon" aria-label="LinkedIn">
                <LinkedinIcon className="h-5 w-5" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                aria-label="Toggle theme"
                onClick={onThemeToggle}
                className="ml-2"
              >
                <SunIcon className="h-5 w-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                <MoonIcon className="absolute h-5 w-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                <span className="sr-only">Toggle theme</span>
              </Button>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-5">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/"
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/all-products"
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  All Products
                </Link>
              </li>
              <li>
                <Link
                  to="/deals"
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  Deals
                </Link>
              </li>
              <li>
                <Link
                  to="/suppliers"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  Suppliers
                </Link>
              </li>
              <li>
                <Link
                  to="/blog"
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  Blog
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          {/* Customer Service */}
          <div>
            <h3 className="text-lg font-bold mb-5">Customer Service</h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/faq"
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  FAQ
                </Link>
              </li>
              <li>
                <Link
                  to="/shipping"
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  Shipping Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/returns"
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  Returns & Refunds
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy"
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  Terms & Conditions
                </Link>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-lg font-bold mb-5">Newsletter</h3>
            <p className="text-muted-foreground mb-5">
              Subscribe to receive updates on new products and special offers.
            </p>
            <div className="flex space-x-3">
              <Input
                type="email"
                placeholder="Your email"
                className="max-w-[220px]"
              />
              <Button>Subscribe</Button>
            </div>
            <div className="mt-8 space-y-3">
              <div className="flex items-center text-muted-foreground">
                <PhoneIcon className="h-4 w-4 mr-2" />
                <span>+91 1234 567 890</span>
              </div>
              <div className="flex items-center text-muted-foreground">
                <MailIcon className="h-4 w-4 mr-2" />
                <span>info@machinesearch.in</span>
              </div>
              <div className="flex items-center text-muted-foreground">
                <MapPinIcon className="h-4 w-4 mr-2" />
                <span>Erode, Tamil Nadu, India</span>
              </div>
            </div>
          </div>
        </div>

        <Separator className="my-8" />

        <div className="flex flex-col md:flex-row justify-between items-center">
          <p className={cn("text-sm text-muted-foreground", isMobile ? 'mb-3' : 'mb-0')}>
            &copy; {new Date().getFullYear()} MachineSearch. All rights
            reserved.
          </p>
          <div className="flex space-x-6">
            <Link
              to="/privacy"
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              Terms of Service
            </Link>
            <Link
              to="/sitemap"
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              Sitemap
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
